/**
 * ClinicTicketsPage — list of the user's CLINIC tickets (customer ↔ clinic).
 *
 * Route: `/clinic-tickets` (ProtectedRoute).
 *
 * The customer ↔ clinic stream, separate from platform support (`/support`).
 * Each row shows the addressed clinic's name + status and is tappable →
 * `/clinic-tickets/:id`. Header has a "New ticket" CTA → `/clinic-tickets/new`.
 *
 * Sort: by `updatedAt` desc, so a fresh clinic reply surfaces a ticket back
 * to the top. Mirrors SupportPage; copy + endpoints differ (clinic domain).
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxIcon from '@mui/icons-material/Inbox';
import ForumIcon from '@mui/icons-material/Forum';

import LanguageToggle from '@/components/LanguageToggle';
import { clinicTicketsApi } from '@/services/api';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';
import { formatDate } from '@/utils/date';
import { localized } from '@/utils/localizedContent';

/** Map a ticket status to a MUI chip color. Shared shape with SupportPage. */
function statusChipColor(status) {
  switch (status) {
    case 'open':        return 'warning';
    case 'in_progress': return 'info';
    case 'resolved':    return 'success';
    case 'closed':      return 'default';
    default:            return 'default';
  }
}

function TicketCard({ ticket, language, t, onOpen }) {
  // clinicName is bilingual JSONB ({ fa, en }); fall back to the code.
  const clinicLabel =
    localized(ticket.clinicName, language) || ticket.clinicCode || '';

  return (
    <Card variant="outlined">
      <CardActionArea onClick={() => onOpen(ticket.id)}>
        <CardContent>
          {/* Top: subject (truncate) + status */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {ticket.subject}
            </Typography>
            {ticket.unreadCount > 0 && (
              <Chip
                size="small"
                color="error"
                label={t('common.unreadCount', { count: ticket.unreadCount })}
                sx={{ flexShrink: 0, fontWeight: 700 }}
              />
            )}
            <Chip
              size="small"
              color={statusChipColor(ticket.status)}
              label={t(`clinicSupport.status.${ticket.status}`, {
                defaultValue: ticket.status,
              })}
              variant="filled"
            />
          </Stack>

          {/* Addressed clinic + category */}
          {clinicLabel && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', mb: 0.25 }}>
              <StorefrontIcon fontSize="inherit" />
              <Typography variant="caption">{clinicLabel}</Typography>
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            {t(`clinicSupport.category.${ticket.category}`, {
              defaultValue: ticket.category,
            })}
          </Typography>

          <Divider sx={{ my: 1 }} />

          {/* Footer: message count + last updated */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ color: 'text.secondary', gap: 1 }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ForumIcon fontSize="small" />
              <Typography variant="caption">
                {t('clinicSupport.list.messageCount', {
                  count: ticket.messageCount ?? ticket.messages?.length ?? 0,
                })}
              </Typography>
            </Stack>
            <Typography variant="caption">
              {formatDate(ticket.updatedAt, language, { style: 'datetime' })}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export function ClinicTicketsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['clinicTickets'],
    queryFn: () => clinicTicketsApi.listTickets(),
    staleTime: 30 * 1000,
  });

  const tickets = data?.data?.tickets ?? [];

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={handleBack} aria-label={t('common.back')}>
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <StorefrontIcon color="primary" />
            <Typography variant="h6" component="h1" fontWeight={700}>
              {t('clinicSupport.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {/* Subtitle + New ticket CTA */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
        >
          {!isError && (
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {t('clinicSupport.subtitle')}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/clinic-tickets/new')}
            sx={{ flexShrink: 0 }}
          >
            {t('clinicSupport.newTicket')}
          </Button>
        </Stack>

        {/* Loading */}
        {isLoading && (
          <Stack spacing={2}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            ))}
          </Stack>
        )}

        {/* Error */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {t('clinicSupport.error.loadFailed')}
          </Alert>
        )}

        {/* Empty state */}
        {!isLoading && !isError && tickets.length === 0 && (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <InboxIcon sx={{ fontSize: 56, opacity: 0.4 }} />
            <Typography variant="subtitle1" textAlign="center" fontWeight={600}>
              {t('clinicSupport.empty.title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 320 }}
            >
              {t('clinicSupport.empty.message')}
            </Typography>
          </Stack>
        )}

        {/* List */}
        {!isLoading && !isError && tickets.length > 0 && (
          <Stack spacing={2}>
            {tickets.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                language={language}
                t={t}
                onOpen={(id) => navigate(`/clinic-tickets/${id}`)}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

export default ClinicTicketsPage;
